import { faBan, faHandshake } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Typography, Box, Popover, Tooltip } from '@vapor/material'
import { Stack, Grid, Paper } from '@mui/material'
import getSymbolFromCurrency from 'currency-symbol-map'
import { LocalizedString } from 'hic-components'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RateRestriction, DailyPriceRq /* useGetWorstBookingPolicyQuery */, CancellationPolicyType, GuaranteeType } from 'src/api/api.generated'
import { Currency } from 'src/api/currency'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { v4 as uuid } from 'uuid'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { useHicCommonDataGridProps, HicDataGridPro } from 'src/app/components/hic-vapor/datagrid/datagrid'
import { DailyPricesRow, ReservationCartRow, ReservationOccupancy, ReservationRateAdditionalService, SelectedReservationChoice, ResourceRateInfo } from '../../form/reservation-new.type'
import UnavailableResourceModal from '../unavailable-resource-modal'
import ReservationChoiceRateInfo from './reservation-choice-rate-info'
import ChargeCategorySection from './charge-category-section'
import ChargeCategorySectionMobile from './charge-category-section-mobile'
import i18n from 'src/locales/i18n'
import { calculateAdditionalServicePrice } from 'src/utils/additional-services-utils'

type ReservationChoiceProps = {
  rateId: string
  rateName: LocalizedString
  rateIsAffiliated: boolean
  includedAdditionalServices: ReservationRateAdditionalService[]
  nights: number
  start: Date
  end: Date
  resourceRates: ResourceRateInfo[] | undefined
  currency: Currency
  occupancy: ReservationOccupancy
  firstInsertedCartRow:
    | {
        rateId: string
        chargeCategoryId: string
      }
    | undefined
  onAddToCart: (reservation: ReservationCartRow, showSuccessMessage: boolean) => void
  navigateToAdditionalServices: () => void
  unavailable: boolean

  // constraints
  minStay?: number
  maxStay?: number
  restrictionsNotMet: RateRestriction[] | null | undefined
  bookingPolicyName: string
  cancellationPolicyType: CancellationPolicyType
  policyDayWithoutPenalty?: number | undefined
  policyPenaltyPercentage?: number | undefined
  policyPayment: { type: GuaranteeType; percentage?: number | null }
  closeToArrival?: string | null
  closeToDeparture?: string | null
}

export default function ReservationChoice(props: ReservationChoiceProps) {
  const { t } = useTranslation()
  const localizeString = useLocalizeString()
  const { sm, md } = useBreakpoints()
  const formatDate = useFormatDate()
  const { hicCommonDataGridProProps } = useHicCommonDataGridProps()

  //#region popover
  const [dailyPricesRows, setDailyPricesRows] = useState<DailyPricesRow[] | undefined>()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const showDailyPrices = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClosePopOver = () => {
    setAnchorEl(null)
  }
  const openPopOver = Boolean(anchorEl)
  //#endregion popover

  const [reservationChoiceState, setReservationChoiceState] = useState<SelectedReservationChoice[]>([])

  const initialTotalPrices = useMemo(() => {
    return props.resourceRates?.map(
      (resourceRate) =>
        resourceRate.dailyPrice.map((res) => res.price).reduce((acc, val) => acc + val, 0) +
        props.includedAdditionalServices.reduce(
          (total, service) =>
            total +
            calculateAdditionalServicePrice({
              adults: props.occupancy.adults,
              children: props.occupancy.children,
              automaticForAdults: service.additionalService.automaticForAdults,
              automaticForChildren: service.additionalService.automaticForChildren,
              price: service.additionalService.price,
              pricingType: service.additionalService.pricingType,
              childPrices: service.additionalService.childServicePrices,
              days: props.nights,
              priceZero: service.priceZero,
            }),
          0,
        ),
    )
  }, [props.resourceRates])

  const initialTotalForRoom = useMemo(() => {
    return props.resourceRates?.map(
      (resourceRate) =>
        resourceRate.dailyPrice.map((res) => res.price).reduce((acc, val) => acc + val, 0) +
        props.includedAdditionalServices.reduce(
          (total, service) =>
            total +
            calculateAdditionalServicePrice({
              adults: props.occupancy.adults,
              children: props.occupancy.children,
              automaticForAdults: service.additionalService.automaticForAdults,
              automaticForChildren: service.additionalService.automaticForChildren,
              price: service.additionalService.price,
              pricingType: service.additionalService.pricingType,
              childPrices: service.additionalService.childServicePrices,
              days: props.nights,
              priceZero: service.priceZero,
            }),
          0,
        ),
    )
  }, [props.resourceRates])

  useEffect(() => {
    const initialState: SelectedReservationChoice[] = []
    props.resourceRates?.forEach((resRate, index) => {
      const initialPrice = initialTotalPrices ? initialTotalPrices[index] : 0
      initialState.push({
        totalPrice: initialPrice,
        initialTotalPrice: initialPrice,
        chargeCategoryId: resRate.chargeCategoryId,
        resourceTypeId: resRate.resourceTypeId,
      })
    })
    setReservationChoiceState(initialState)
  }, [props.resourceRates])

  // const { data: worstPolicyData } = useGetWorstBookingPolicyQuery({
  //   skip: !props.bookingPolicyOverride || !initialTotalPrices,
  //   variables: {
  //     input: {
  //       totalAmount: initialTotalPrices?.[0] ?? 0,
  //       firstNightAmount: props?.resourceRates?.[0]?.dailyPrice?.[0]?.price ?? 0,
  //       policies: props.bookingPolicyOverride ? [props.bookingPolicy, props.bookingPolicyOverride] : [props.bookingPolicy],
  //     },
  //   },
  // })
  // const worstPolicy = worstPolicyData?.getWorstBookingPolicy ?? props.bookingPolicy

  const [rateIsStopSell, setRateIsStopSell] = useState(false)
  const reservationChoiceHasError = useMemo(() => props.unavailable || (props.restrictionsNotMet ?? []).length > 0 || rateIsStopSell, [props.unavailable, props.restrictionsNotMet, rateIsStopSell])

  const [openErrorsDialog, setOpenErrorsDialog] = useState<() => void>()

  const addChoiceToCart = (resourceRate: ResourceRateInfo, choiceId: number, showSuccessMessage: boolean) => {
    const totalPrice =
      reservationChoiceState[choiceId].totalPrice -
      props.includedAdditionalServices?.reduce(
        (total, service) =>
          total +
          calculateAdditionalServicePrice({
            adults: props.occupancy.adults,
            children: props.occupancy.children,
            automaticForAdults: service.additionalService.automaticForAdults,
            automaticForChildren: service.additionalService.automaticForChildren,
            price: service.additionalService.price,
            pricingType: service.additionalService.pricingType,
            childPrices: service.additionalService.childServicePrices,
            days: props.nights,
            priceZero: service.priceZero,
          }),
        0,
      )

    props.onAddToCart(
      {
        id: uuid(),
        unavailable: resourceRate.dailyPrice.findIndex((dp) => dp.restriction === 'STOPSELL') >= 0,
        isFirstInsertion: props.firstInsertedCartRow === undefined,
        isSummary: false,
        rate: {
          id: props.rateId,
          name: props.rateName,
        },
        policy: resourceRate.policy
          ? {
              additionalNote: resourceRate.policy?.additionalNote,
              name: resourceRate.policy?.name,
              cancellationPolicy: {
                daysWithoutPenalties: resourceRate.policy?.cancellationPolicy.daysWithoutPenalties,
                hourWithoutPenalties: resourceRate.policy?.cancellationPolicy.hourWithoutPenalties,
                penaltyType: resourceRate.policy?.cancellationPolicy.penaltyType,
                percentage: resourceRate.policy?.cancellationPolicy.percentage,
                type: resourceRate.policy?.cancellationPolicy.type,
              },
              noShowRule: {
                percentage: resourceRate.policy?.noShowRule.percentage,
                type: resourceRate.policy?.noShowRule.type,
              },
              paymentRule: {
                guaranteedCreditCard: resourceRate.policy?.paymentRule.guaranteedCreditCard,
                paymentsAcceptedId: resourceRate.policy?.paymentRule.paymentsAcceptedId,
                percentage: resourceRate.policy?.paymentRule.percentage,
                type: resourceRate.policy?.paymentRule.type,
                installments: resourceRate.policy?.paymentRule.installments?.map((i) => {
                  return {
                    dayFromCheckin: i.dayFromCheckin,
                    order: i.order,
                    value: i.value,
                  }
                }),
              },
            }
          : undefined,
        chargeCategory: {
          id: resourceRate.chargeCategoryId,
          name: resourceRate.chargeCategoryName,
        },
        occupancy: props.occupancy,
        initialPrice: Math.round(reservationChoiceState[choiceId].initialTotalPrice / props.nights),
        averagePrice: Math.round(totalPrice / props.nights),
        initialTotalPrice: reservationChoiceState[choiceId].initialTotalPrice,
        totalPrice: reservationChoiceState[choiceId].totalPrice, // not using totalPrice because this field is used to edit the total price with the services price
        initialDailyPrices: resourceRate.dailyPrice,
        nights: props.nights,
        start: props.start,
        end: props.end,
        resourceType: {
          id: resourceRate.resourceTypeId,
          name: resourceRate.resourceTypeName,
          code: resourceRate.resourceTypeCode,
        },
        includedAdditionalServices: props.includedAdditionalServices?.map((additionalService) => {
          return {
            id: additionalService.additionalService.id,
            name: additionalService.additionalService.name,
            description: additionalService.additionalService.description,
            price: additionalService.additionalService.price,
            added: false,
            priceZero: additionalService.priceZero,
            pricingType: additionalService.additionalService.pricingType,
            adults: props.occupancy.adults,
            children: props.occupancy.children.map((c) => c.quantity).reduce((acc, val) => acc + val, 0),
            nights: props.nights,
            currency: props.currency,
            automaticForAdults: additionalService.additionalService.automaticForAdults,
            automaticForChildren: additionalService.additionalService.automaticForChildren,
            childPrices: additionalService.additionalService.childServicePrices,
            minStay: additionalService.additionalService.minStay ?? 1,
          }
        }),
      },
      showSuccessMessage,
    )
    setOpenErrorsDialog(undefined)
  }

  const fastAddChoiceToCart = (resourceRate: ResourceRateInfo, choiceId: number) => {
    addChoiceToCart(resourceRate, choiceId, false)
    props.navigateToAdditionalServices()
  }

  return (
    <>
      <UnavailableResourceModal
        open={openErrorsDialog !== undefined}
        handleClose={() => setOpenErrorsDialog(undefined)}
        errors={{
          resourceUnavailable: props.unavailable,
          rateIsStopSell: rateIsStopSell,
          restrictionsNotMet: props.restrictionsNotMet,
        }}
        onConfirm={() => openErrorsDialog?.()}
      />
      <Paper variant="outlined" elevation={0} sx={{ mb: 0.25 }}>
        <Grid container>
          <Grid item xs={12} sm={3} sx={{ borderRightColor: 'var(--AzureishWhite)', borderRightWidth: '4px', borderRightStyle: 'solid' }}>
            <Stack direction="column" justifyContent={{ xs: 'space-between', sm: 'start' }} spacing={0.5} pt={1} pr={1} pl={1}>
              <Tooltip title={localizeString(props.rateName)} placement="bottom-start">
                <div style={{ pointerEvents: 'auto' }}>
                  <Stack direction="row" spacing={0.5}>
                    {props.rateIsAffiliated && <FontAwesomeIcon icon={faHandshake} color="var(--DarkImperialBlue)" style={{ marginTop: '1px' }} />}
                    <Typography
                      variant="titleXSmall"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: '2',
                        WebkitBoxOrient: 'vertical',
                        lineHeight: '18px',
                        marginBottom: '4px',
                      }}
                    >
                      <b>{localizeString(props.rateName)}</b>
                    </Typography>
                  </Stack>
                </div>
              </Tooltip>

              <ReservationChoiceRateInfo {...props} rateIsStopSell={rateIsStopSell} />
            </Stack>
          </Grid>
          <Grid item xs={12} sm={9} sx={{ justifyContent: 'end', display: 'flex' }} style={sm ? { height: '100%' } : undefined}>
            <Stack sx={{ width: '100%', maxWidth: 1000, p: 1, justifyContent: 'center' }}>
              {props.resourceRates?.map((resourceRate, index, { length }) => {
                const isTheLastChoice = index === length - 1
                const stopSell = resourceRate.dailyPrice.findIndex((dp) => dp.restriction === 'STOPSELL') >= 0
                if (!rateIsStopSell && stopSell) setRateIsStopSell(true)

                // static rate can have a dailyPrice: []
                if (!resourceRate.dailyPrice.length || resourceRate.restrictionsNotMet?.includes('STOPSELL_CHARGE')) {
                  return null
                }

                return (
                  <Stack
                    direction="column"
                    key={'resourceRate' + resourceRate.resourceTypeName + index}
                    style={isTheLastChoice ? undefined : { borderBottomColor: 'var(--AzureishWhite)', borderBottomWidth: '4px', borderBottomStyle: 'solid' }}
                  >
                    {md && (
                      <ChargeCategorySection
                        resourceRate={resourceRate}
                        index={index}
                        initialTotalForRoom={initialTotalForRoom}
                        reservationChoiceState={reservationChoiceState}
                        stopSell={stopSell}
                        reservationChoiceHasError={reservationChoiceHasError}
                        setReservationChoiceState={setReservationChoiceState}
                        setOpenErrorsDialog={setOpenErrorsDialog}
                        addChoiceToCart={(resRate, choiceId) => addChoiceToCart(resRate, choiceId, true)}
                        fastAddChoiceToCart={fastAddChoiceToCart}
                        showDailyPrices={showDailyPrices}
                        setDailyPricesRows={setDailyPricesRows}
                      />
                    )}
                    {!md && (
                      <ChargeCategorySectionMobile
                        resourceRate={resourceRate}
                        index={index}
                        initialTotalForRoom={initialTotalForRoom}
                        reservationChoiceState={reservationChoiceState}
                        stopSell={stopSell}
                        reservationChoiceHasError={reservationChoiceHasError}
                        setReservationChoiceState={setReservationChoiceState}
                        setOpenErrorsDialog={setOpenErrorsDialog}
                        addChoiceToCart={(resRate, choiceId) => addChoiceToCart(resRate, choiceId, true)}
                        fastAddChoiceToCart={fastAddChoiceToCart}
                        showDailyPrices={showDailyPrices}
                        setDailyPricesRows={setDailyPricesRows}
                      />
                    )}
                  </Stack>
                )
              })}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Popover
        open={openPopOver}
        anchorEl={anchorEl}
        onClose={handleClosePopOver}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box height="350px" width="300px" p={1}>
          <Stack direction="column">
            <Typography variant="titleSmall" align="center">
              {t('pms.booking.reservation_new.daily_prices').charAt(0).toUpperCase() + t('pms.booking.reservation_new.daily_prices').slice(1)}
            </Typography>
            {(dailyPricesRows ?? []).length > 1 && (
              <Typography variant="caption" align="center">
                {dailyPricesRows ? formatDate(dailyPricesRows[0].dailyPrice.day) : ''} - {dailyPricesRows ? formatDate(dailyPricesRows[dailyPricesRows?.length - 1].dailyPrice.day) : ''}
              </Typography>
            )}
            <HicDataGridPro
              sx={{ mt: 1 }}
              {...hicCommonDataGridProProps}
              hideFooter
              disableColumnMenu
              columnBuffer={2}
              autoHeight
              columns={(() => {
                const columns = [
                  {
                    field: 'data',
                    headerName: t('pms.booking.reservation_new.date'),
                    flex: 1.1,
                    sortable: false,
                    renderCell: (params: { row: { dailyPrice: DailyPriceRq; discount?: number | undefined; stopSell: boolean } }) => {
                      const { dailyPrice } = params.row
                      return dailyPrice.day.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'numeric', year: 'numeric' })
                    },
                  },
                  {
                    field: 'price',
                    headerName: t('pms.booking.reservation_new.price'),
                    flex: 1,
                    sortable: false,
                    renderCell: (params: { row: { dailyPrice: DailyPriceRq; discount?: number | undefined; stopSell: boolean } }) => {
                      const { dailyPrice, discount } = params.row
                      if (discount) {
                        return (
                          <div>
                            {getSymbolFromCurrency(props.currency)}
                            {discount / 100}
                            <del>
                              {getSymbolFromCurrency(props.currency)}
                              {dailyPrice.price / 100}
                            </del>
                          </div>
                        )
                      } else {
                        return (
                          <>
                            {getSymbolFromCurrency(props.currency)}
                            {dailyPrice.price / 100}
                          </>
                        )
                      }
                    },
                  },
                ]
                const hasStopSell = dailyPricesRows?.findIndex((row) => row.stopSell) ?? -1
                if (hasStopSell >= 0) {
                  columns.push({
                    field: 'stopSell',
                    headerName: t('pms.booking.reservation_new.stop_sell'),
                    flex: 1,
                    sortable: false,
                    renderCell: (params) => {
                      const { stopSell } = params.row
                      return stopSell ? (
                        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
                          <FontAwesomeIcon icon={faBan} color="red" style={{ width: 16, height: 16, display: 'flex' }} />
                        </Stack>
                      ) : (
                        <></>
                      )
                    },
                  })
                }
                return columns
              })()}
              rows={
                dailyPricesRows?.map((row) => {
                  return {
                    id: uuid(),
                    ...row,
                  }
                }) ?? []
              }
            />
          </Stack>
        </Box>
      </Popover>
    </>
  )
}
